import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
    ActivatedRoute,
    Router,
} from '@angular/router';
import { AppStateFacade } from '@app-state';
import {
    PageMetadata,
    PageMetadataRegistry,
} from '@app/router';
import { AccessCode } from '@models';
import { TranslateService } from '@ngx-translate/core';

import {
    combineLatest,
    merge,
    Observable,
    Subject,
} from 'rxjs';
import {
    filter,
    map,
    switchMap,
    switchMapTo,
    takeUntil,
    tap,
} from 'rxjs/operators';

const CODE_TO_TRANSLATION: Partial<Record<AccessCode, string>> = {
    [AccessCode.NotFound]: 'notfound_page',
    [AccessCode.FeatureNotEnabled]: 'feature_not_enabled_page',
    [AccessCode.FolderRequired]: 'access_denied_folder_required',
    [AccessCode.PermissionRequired]: 'access_denied_general',
    [AccessCode.FeatureNotLicensed]: 'feature_not_licensed_page',
    [AccessCode.ClassicFolderRequired]: 'classic_folder_required_message',
};

@Component({
    templateUrl: 'restricted-access.page.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RestrictedAccessPageComponent implements OnDestroy {
    public code$: Observable<AccessCode>;
    public page$: Observable<PageMetadata | undefined>;
    public message$: Observable<string>;

    private _destroyed$ = new Subject();

    constructor(
        route: ActivatedRoute,
        pageRegistry: PageMetadataRegistry,
        title: Title,
        appState: AppStateFacade,
        translate: TranslateService,
        router: Router,
    ) {
        this.code$ = route.queryParamMap
            .pipe(
                map(params => {
                    const reason = params.get('reason');
                    const reasonAsNumber = Number(reason);

                    if (
                        reason == null ||
                        isNaN(reasonAsNumber)
                    ) { return AccessCode.NotFound; }

                    return reasonAsNumber as AccessCode;
                }),
            );

        this.page$ = route.queryParamMap
            .pipe(
                map(params => {
                    const target = params.get('target') || '';

                    return pageRegistry.find(target);
                }),
            );

        this.message$ = combineLatest([this.code$, this.page$])
            .pipe(
                map(([code, page]) => this._extractTranslationKey(code, page)),
                switchMap(translateKey => translate.stream(translateKey || '')),
                tap(translatedMessage => title.setTitle(`${translatedMessage} - ${appState.authConfig.value.serverSettings.baseTitle}`)),
            );

        const changes$ = merge([
            appState.user.permissions.change$,
            appState.user.folder.change$,
        ]);

        changes$
            .pipe(
                switchMapTo(
                    combineLatest([
                        this.code$,
                        this.page$,
                    ]),
                ),
                filter(([reason, page]) =>
                    !!page && [AccessCode.ClassicFolderRequired, AccessCode.FolderRequired, AccessCode.PermissionRequired].includes(reason),
                ),
                takeUntil(this._destroyed$),
            ).subscribe(([_, page]) => {
                router.navigate([page!.routerLink]);
            });
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    private _extractTranslationKey(code: AccessCode, page?: PageMetadata) {
        if (page &&
            [AccessCode.ClassicFolderRequired, AccessCode.FolderRequired].includes(code) &&
            page.folderTypeDependencyMessage) {
            return page.folderTypeDependencyMessage;
        }
        return CODE_TO_TRANSLATION[code];
    }
}
